import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Show, SimpleShowLayout, DateField, useShowController, useNotify, useRedirect, useGetIdentity } from 'react-admin';
import { ordersProxy } from './apiGateway';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Avatar,
    List,
    ListItem,
    ListItemText,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    CircularProgress,
    Divider,
    Skeleton,
    Link as MuiLink,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    OrderPaymentInfoInternalResponse,
    ProgressOrderStatus,
    SellerReceiptContext,
    SellerReceiptCreationAuditItem,
} from './types/orders';
import { canOpenPlatformUserShow, getPlatformUserShowUrl } from './platformUserShowLink';
import { progressOrderStatusBanners } from './progressOrderStatusMeta';

const ORDER_PAYMENT_VISIBLE_ROLES = new Set(['moderator', 'moderator-admin', 'manager-admin']);
const SELLER_RECEIPT_CREATE_ROLES = new Set(['moderator-admin', 'manager-admin']);
const ORDER_PAYMENT_DELAY_MS = 24 * 60 * 60 * 1000;
const SELLER_RECEIPT_POLL_INTERVAL_MS = 5000;

const formatOptionalValue = (value?: string | null) => {
    if (!value) {
        return 'Не указано';
    }
    return value;
};

const formatDateTimeValue = (value?: string | null) => {
    if (!value) {
        return 'Не указано';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return value;
    }

    return parsedDate.toLocaleString('ru-RU');
};

const formatPayoutAmount = (value?: number | null) => {
    if (!Number.isFinite(value)) {
        return 'Не указано';
    }
    return `${new Intl.NumberFormat('ru-RU').format(Number(value))} ₽`;
};

const formatCurrencyValue = (value: number) => new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
}).format(value);

const normalizeNumber = (value: unknown) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const roundUpTo2 = (value: number) => Math.ceil(value * 100) / 100;

const formatOrderPayoutState = (state?: string | null) => {
    const normalizedState = typeof state === 'string' ? state.trim().toUpperCase() : '';
    if (normalizedState === 'CREATED') {
        return 'Платежка создана';
    }
    if (normalizedState === 'REVERSED') {
        return 'Возврат покупателю со счета заморозки';
    }
    if (normalizedState === 'REFUND') {
        return 'Возврат покупателю с нашего счета';
    }
    if (normalizedState === 'REFUND_ERROR') {
        return 'Ошибка возврата';
    }
    if (normalizedState === 'FINISHED') {
        return 'Заказ завершен, ожидается выплата';
    }
    if (normalizedState === 'PENDING_PAYOUT') {
        return 'Запрос на выплату';
    }
    if (normalizedState === 'PAID') {
        return 'Выплата успешно выполнена';
    }
    if (normalizedState === 'PAID_ERROR') {
        return 'Ошибка выплаты';
    }
    return formatOptionalValue(state);
};

const formatOrderPayoutErrorState = (errorState?: string | null) => {
    const normalizedErrorState = typeof errorState === 'string' ? errorState.trim().toUpperCase() : '';
    if (!normalizedErrorState) {
        return 'Не указано';
    }
    if (normalizedErrorState === 'PAYOUT_CARD_NOT_FOUND') {
        return 'Не найдена привязанная карта для выплаты';
    }
    if (normalizedErrorState === 'PAYOUT_CARD_INACTIVE') {
        return 'Привязанная карта для выплаты просрочена';
    }
    return formatOptionalValue(errorState);
};

const getPlannedPayoutDate = (finishedAt?: string | null) => {
    if (!finishedAt) {
        return null;
    }

    const finishedDate = new Date(finishedAt);
    if (Number.isNaN(finishedDate.getTime())) {
        return null;
    }

    return new Date(finishedDate.getTime() + ORDER_PAYMENT_DELAY_MS);
};

const getReceiptErrorMessage = (error: unknown) => {
    const payload = (error as { payload?: unknown })?.payload;
    const payloadError = payload && typeof payload === 'object'
        ? (payload as { error?: string | { id?: string; message?: string } }).error
        : null;
    const errorId = payloadError && typeof payloadError === 'object'
        ? payloadError.id?.trim()
        : undefined;
    const errorLabels: Record<string, string> = {
        BAD_REQUEST: 'Некорректный идентификатор платежа',
        PAYMENT_WAS_NOT_FOUND: 'Платёж не найден',
        SELLER_RECEIPT_CONFLICT: 'Чек можно сформировать только после успешной выплаты',
        SELLER_RECEIPT_NOT_ELIGIBLE: 'Заказ или выплата ещё не готовы для формирования чека',
        DIGITAL_KASSA_RECEIPT_REJECTED: 'Провайдер отклонил данные чека',
        DIGITAL_KASSA_UNAVAILABLE: 'Сервис фискализации временно недоступен',
        INTERNAL_ERROR: 'Произошла внутренняя ошибка сервиса'
    };
    if (errorId && errorLabels[errorId]) {
        return errorLabels[errorId];
    }
    if (typeof payloadError === 'string' && payloadError.trim()) {
        return payloadError;
    }
    if (payloadError && typeof payloadError === 'object' && payloadError.message?.trim()) {
        return payloadError.message;
    }
    return error instanceof Error && error.message
        ? error.message
        : 'Не удалось выполнить запрос';
};

const getReceiptOutcomeLabel = (outcome: SellerReceiptCreationAuditItem['outcome']) => {
    if (outcome === 'SUCCESS') {
        return 'Успешно';
    }
    if (outcome === 'ERROR') {
        return 'Ошибка';
    }
    return 'В процессе';
};

export const OrderShow = () => {
    const { record, isLoading, error } = useShowController();
    const notify = useNotify();
    const redirect = useRedirect();
    const { identity } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const canOpenUserShow = canOpenPlatformUserShow(currentUser?.role);
    const canViewOrderPaymentInfo = ORDER_PAYMENT_VISIBLE_ROLES.has(currentUser?.role || '');
    const canCreateSellerReceipt = SELLER_RECEIPT_CREATE_ROLES.has(currentUser?.role || '');
    const currentOrderId = typeof record?.orderId === 'string' ? record.orderId.trim() : '';

    // Состояния для диалогового окна
    const [shipmentDialogOpen, setShipmentDialogOpen] = useState(false);
    const [returnDialogOpen, setReturnDialogOpen] = useState(false);
    const [pvzDialogOpen, setPvzDialogOpen] = useState(false);
    const [arrivalDateDialogOpen, setArrivalDateDialogOpen] = useState(false);
    const [returnPvzDialogOpen, setReturnPvzDialogOpen] = useState(false);
    const [deliveryArrivalDateDialogOpen, setDeliveryArrivalDateDialogOpen] = useState(false);
    const [initiateReturnDialogOpen, setInitiateReturnDialogOpen] = useState(false);
    const [receivedConfirmationDialogOpen, setReceivedConfirmationDialogOpen] = useState(false);
    const [cancelledByExpiredPickingDialogOpen, setCancelledByExpiredPickingDialogOpen] = useState(false);
    const [cancelledAfterDeliveredDialogOpen, setCancelledAfterDeliveredDialogOpen] = useState(false);
    const [deliveredBackConfirmationDialogOpen, setDeliveredBackConfirmationDialogOpen] = useState(false);
    const [retryDeliveryOrderDialogOpen, setRetryDeliveryOrderDialogOpen] = useState(false);
    
    // Состояния для кнопок разработки
    const [devTempErrorDialogOpen, setDevTempErrorDialogOpen] = useState(false);
    const [devFatalErrorDialogOpen, setDevFatalErrorDialogOpen] = useState(false);
    
    // Состояние для загрузки
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Состояние для диалога доставки
    const [deliveryTimeDialogOpen, setDeliveryTimeDialogOpen] = useState(false);
    const [approximateDeliveryDate, setApproximateDeliveryDate] = useState('');
    const [trackNumber, setTrackNumber] = useState('');
    const [providerOrderId, setProviderOrderId] = useState('');
    const [pvzCode, setPvzCode] = useState('');
    const [pvzCity, setPvzCity] = useState('');
    const [pvzAddress, setPvzAddress] = useState('');
    const [pvzWorkingHours, setPvzWorkingHours] = useState('');
    const [pvzLatitude, setPvzLatitude] = useState('');
    const [pvzLongitude, setPvzLongitude] = useState('');
    const [returnPvzCode, setReturnPvzCode] = useState('');
    const [returnPvzCity, setReturnPvzCity] = useState('');
    const [returnPvzAddress, setReturnPvzAddress] = useState('');
    const [returnPvzWorkingHours, setReturnPvzWorkingHours] = useState('');
    const [returnPvzLatitude, setReturnPvzLatitude] = useState('');
    const [returnPvzLongitude, setReturnPvzLongitude] = useState('');
    const [arrivalDate, setArrivalDate] = useState('');
    const [deliveryArrivalDate, setDeliveryArrivalDate] = useState('');
    const [returnTrackNumber, setReturnTrackNumber] = useState('');
    const [returnProviderOrderId, setReturnProviderOrderId] = useState('');
    const [returnDate, setReturnDate] = useState('');
    const [senderFullName, setSenderFullName] = useState('');
    const [senderPhone, setSenderPhone] = useState('');
    const [paymentInfo, setPaymentInfo] = useState<OrderPaymentInfoInternalResponse | null>(null);
    const [paymentInfoLoading, setPaymentInfoLoading] = useState(false);
    const [paymentInfoError, setPaymentInfoError] = useState<string | null>(null);
    const [receiptExpanded, setReceiptExpanded] = useState(false);
    const [receiptHasLoaded, setReceiptHasLoaded] = useState(false);
    const [receiptContext, setReceiptContext] = useState<SellerReceiptContext | null>(null);
    const [receiptLoading, setReceiptLoading] = useState(false);
    const [receiptCreating, setReceiptCreating] = useState(false);
    const [receiptError, setReceiptError] = useState<string | null>(null);
    const [receiptErrorSource, setReceiptErrorSource] = useState<'load' | 'create' | null>(null);
    const [receiptPollingActive, setReceiptPollingActive] = useState(false);
    const [receiptAudit, setReceiptAudit] = useState<SellerReceiptCreationAuditItem[]>([]);
    const [receiptAuditLoading, setReceiptAuditLoading] = useState(false);
    const [receiptAuditError, setReceiptAuditError] = useState<string | null>(null);
    const receiptRequestInFlightRef = useRef(false);
    const receiptRequestSequenceRef = useRef(0);
    const receiptAuditSequenceRef = useRef(0);

    useEffect(() => {
        let isActive = true;

        if (!canViewOrderPaymentInfo || !currentOrderId) {
            setPaymentInfo(null);
            setPaymentInfoError(null);
            setPaymentInfoLoading(false);
            return () => {
                isActive = false;
            };
        }

        setPaymentInfoLoading(true);
        setPaymentInfoError(null);

        ordersProxy.getPaymentInfo(currentOrderId)
            .then((responseData) => {
                if (!isActive) {
                    return;
                }

                const payload = responseData?.body && typeof responseData.body === 'object'
                    ? responseData.body
                    : responseData;

                setPaymentInfo((payload ?? null) as OrderPaymentInfoInternalResponse | null);
            })
            .catch((loadError: unknown) => {
                if (!isActive) {
                    return;
                }

                const status = Number((loadError as { status?: unknown })?.status);
                if (status === 404) {
                    setPaymentInfo(null);
                    setPaymentInfoError(null);
                    return;
                }

                console.error('Ошибка при загрузке информации о выплате:', loadError);
                setPaymentInfo(null);
                setPaymentInfoError('Не удалось загрузить информацию о выплате');
            })
            .finally(() => {
                if (isActive) {
                    setPaymentInfoLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [canViewOrderPaymentInfo, currentOrderId]);

    useEffect(() => {
        receiptRequestSequenceRef.current += 1;
        receiptAuditSequenceRef.current += 1;
        receiptRequestInFlightRef.current = false;
        setReceiptExpanded(false);
        setReceiptHasLoaded(false);
        setReceiptContext(null);
        setReceiptLoading(false);
        setReceiptCreating(false);
        setReceiptError(null);
        setReceiptErrorSource(null);
        setReceiptPollingActive(false);
        setReceiptAudit([]);
        setReceiptAuditLoading(false);
        setReceiptAuditError(null);
    }, [currentOrderId]);

    const loadReceiptContext = useCallback(async (options?: {
        polling?: boolean;
        keepPollingWhenMissing?: boolean;
    }) => {
        if (!currentOrderId || receiptRequestInFlightRef.current) {
            return null;
        }

        const polling = options?.polling === true;
        const requestSequence = ++receiptRequestSequenceRef.current;
        receiptRequestInFlightRef.current = true;
        if (!polling) {
            setReceiptLoading(true);
        }

        try {
            const context = await ordersProxy.getReceiptContext(currentOrderId);
            if (requestSequence !== receiptRequestSequenceRef.current) {
                return null;
            }

            setReceiptContext(context);
            setReceiptError(null);
            setReceiptErrorSource(null);
            const receiptStatus = context.receipt?.status?.trim().toUpperCase();
            if (context.eligibility !== 'ELIGIBLE') {
                setReceiptPollingActive(false);
            } else if (receiptStatus === 'PENDING') {
                setReceiptPollingActive(true);
            } else if (context.receipt || !options?.keepPollingWhenMissing) {
                setReceiptPollingActive(false);
            }
            return context;
        } catch (loadError: unknown) {
            if (requestSequence !== receiptRequestSequenceRef.current) {
                return null;
            }

            console.error('Ошибка при загрузке чека:', loadError);
            setReceiptError(getReceiptErrorMessage(loadError));
            setReceiptErrorSource('load');
            const status = Number((loadError as { status?: unknown })?.status);
            if (polling && (status === 400 || status === 403)) {
                setReceiptPollingActive(false);
            }
            return null;
        } finally {
            if (requestSequence === receiptRequestSequenceRef.current) {
                receiptRequestInFlightRef.current = false;
                if (!polling) {
                    setReceiptLoading(false);
                }
            }
        }
    }, [currentOrderId]);

    const loadReceiptAudit = useCallback(async () => {
        if (!currentOrderId) {
            return;
        }

        const requestSequence = ++receiptAuditSequenceRef.current;
        setReceiptAuditLoading(true);
        setReceiptAuditError(null);
        try {
            const response = await ordersProxy.getReceiptAudit(currentOrderId, 50);
            if (requestSequence !== receiptAuditSequenceRef.current) {
                return;
            }
            setReceiptAudit(Array.isArray(response?.items) ? response.items as SellerReceiptCreationAuditItem[] : []);
        } catch (loadError: unknown) {
            if (requestSequence !== receiptAuditSequenceRef.current) {
                return;
            }
            console.error('Ошибка при загрузке аудита чека:', loadError);
            setReceiptAuditError(getReceiptErrorMessage(loadError));
        } finally {
            if (requestSequence === receiptAuditSequenceRef.current) {
                setReceiptAuditLoading(false);
            }
        }
    }, [currentOrderId]);

    useEffect(() => {
        if (!receiptPollingActive || !currentOrderId) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            void loadReceiptContext({ polling: true, keepPollingWhenMissing: true });
        }, SELLER_RECEIPT_POLL_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [currentOrderId, loadReceiptContext, receiptPollingActive]);

    const handleReceiptAccordionChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        setReceiptExpanded(expanded);
        if (expanded && !receiptHasLoaded) {
            setReceiptHasLoaded(true);
            void Promise.all([
                loadReceiptContext(),
                loadReceiptAudit()
            ]);
        }
    };

    const handleRefreshReceipt = () => {
        void Promise.all([
            loadReceiptContext({ keepPollingWhenMissing: receiptPollingActive }),
            loadReceiptAudit()
        ]);
    };

    const handleCreateReceipt = async () => {
        if (!currentOrderId || receiptCreating) {
            return;
        }

        setReceiptCreating(true);
        setReceiptError(null);
        setReceiptErrorSource(null);
        try {
            const result = await ordersProxy.createReceipt(currentOrderId);
            notify(
                result.status === 202
                    ? 'Формирование чека запущено'
                    : 'Запрос на формирование чека выполнен',
                { type: 'success' }
            );
            setReceiptPollingActive(true);
            await Promise.all([
                loadReceiptContext({ keepPollingWhenMissing: true }),
                loadReceiptAudit()
            ]);
        } catch (createError: unknown) {
            console.error('Ошибка при формировании чека:', createError);
            const message = getReceiptErrorMessage(createError);
            setReceiptError(message);
            setReceiptErrorSource('create');
            notify(message, { type: 'error' });
            await loadReceiptAudit();
        } finally {
            setReceiptCreating(false);
        }
    };

    const formatSenderPhone = (value: string) => {
        const digits = value.replace(/\D/g, '');
        if (!digits) {
            return '';
        }

        let normalized = digits;
        if (normalized.startsWith('8')) {
            normalized = `7${normalized.slice(1)}`;
        }
        if (!normalized.startsWith('7')) {
            normalized = `7${normalized}`;
        }
        normalized = normalized.slice(0, 11);

        return `+${normalized}`;
    };

    const handleSenderPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSenderPhone(formatSenderPhone(event.target.value));
    };

    const generateRandomSenderInfo = () => {
        const firstNames = ['Иван', 'Мария', 'Алексей', 'Екатерина', 'Никита', 'Ольга'];
        const lastNames = ['Иванов', 'Петрова', 'Сидоров', 'Кузнецова', 'Смирнов', 'Новикова'];
        const middleNames = ['Иванович', 'Петровна', 'Алексеевич', 'Сергеевна', 'Никитич', 'Олеговна'];

        const fullName = `${lastNames[Math.floor(Math.random() * lastNames.length)]} ` +
            `${firstNames[Math.floor(Math.random() * firstNames.length)]} ` +
            `${middleNames[Math.floor(Math.random() * middleNames.length)]}`;
        const phone = `+7${Array.from({ length: 10 }, () => Math.floor(Math.random() * 10)).join('')}`;

        setSenderFullName(fullName);
        setSenderPhone(phone);
    };

    // Функция для проверки частичного заполнения ПВЗ
    const isPvzPartiallyFilled = () => {
        const pvzFields = [
            returnPvzCode.trim(),
            returnPvzCity.trim(),
            returnPvzAddress.trim(),
            returnPvzWorkingHours.trim(),
            returnPvzLatitude.trim(),
            returnPvzLongitude.trim()
        ];
        
        const filledCount = pvzFields.filter(field => field !== '').length;
        return filledCount > 0 && filledCount < 6;
    };

    // Мапа статусов и их настроек (баннеры, цвета, метки)
    const statusBanners = progressOrderStatusBanners;

    if (isLoading) {
        return (
            <Show title="Загрузка заказа...">
                <SimpleShowLayout>
                    <Card>
                        <CardContent>
                            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                {[...Array(6)].map((_, index) => (
                                    <Box key={index}>
                                        <Skeleton variant="text" width="40%" height={20} />
                                        <Skeleton variant="text" width="80%" height={24} />
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                {[...Array(3)].map((_, index) => (
                                    <Box key={index}>
                                        <Skeleton variant="text" width="60%" height={20} />
                                        <Skeleton variant="text" width="90%" height={24} />
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                {[...Array(4)].map((_, index) => (
                                    <Box key={index}>
                                        <Skeleton variant="text" width="30%" height={20} />
                                        <Skeleton variant="text" width="70%" height={24} />
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="45%" height={32} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                {[...Array(6)].map((_, index) => (
                                    <Box key={index}>
                                        <Skeleton variant="text" width="50%" height={20} />
                                        <Skeleton variant="text" width="85%" height={24} />
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="35%" height={32} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                {[...Array(3)].map((_, index) => (
                                    <Box key={index}>
                                        <Skeleton variant="text" width="40%" height={20} />
                                        <Skeleton variant="text" width="75%" height={24} />
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="55%" height={32} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <Skeleton variant="circular" width={56} height={56} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                                    <Skeleton variant="text" width="30%" height={20} />
                                    <Skeleton variant="text" width="60%" height={24} />
                                    <Skeleton variant="text" width="20%" height={20} />
                                    <Skeleton variant="text" width="50%" height={24} />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Skeleton variant="circular" width={56} height={56} />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                                    <Skeleton variant="text" width="35%" height={20} />
                                    <Skeleton variant="text" width="65%" height={24} />
                                    <Skeleton variant="text" width="25%" height={20} />
                                    <Skeleton variant="text" width="55%" height={24} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                {[...Array(4)].map((_, index) => (
                                    <Box key={index}>
                                        <Skeleton variant="text" width="45%" height={20} />
                                        <Skeleton variant="text" width="80%" height={24} />
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>

                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {[...Array(4)].map((_, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                                        <Skeleton variant="text" width="30%" height={20} />
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                </SimpleShowLayout>
            </Show>
        );
    }
    if (error) {
        notify('Ошибка при загрузке данных', { type: 'error' });
        return <div>Ошибка при загрузке данных</div>;
    }
    if (!record) return <div>Данные не найдены</div>;

    const handleShipmentSubmit = async () => {
        if (isSubmitting) return; // Предотвращаем повторные запросы
        
        setIsSubmitting(true);
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'WAITING_FOR_SHIPMENT',
                deliveryOrderId: providerOrderId,
                trackNumber: trackNumber
            });
            
            setShipmentDialogOpen(false);
            setTrackNumber('');
            setProviderOrderId('');
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReturnSubmit = async () => {
        try {
            // Подготавливаем данные для отправки
            const updateData: any = {
                status: 'ORDER_IS_RETURNING_TO_SELLER',
                deliveryOrderId: providerOrderId,
                trackNumber: trackNumber
            };
            
            // Проверяем, заполнены ли все поля ПВЗ
            const isPvzComplete = returnPvzCode.trim() && returnPvzCity.trim() && 
                                returnPvzAddress.trim() && returnPvzWorkingHours.trim() && 
                                returnPvzLatitude.trim() && returnPvzLongitude.trim();
            
            if (isPvzComplete) {
                updateData.pvzInfo = {
                    code: returnPvzCode,
                    city: returnPvzCity,
                    address: returnPvzAddress,
                    workTime: returnPvzWorkingHours,
                    point: {
                        latitude: parseFloat(returnPvzLatitude),
                        longitude: parseFloat(returnPvzLongitude)
                    }
                };
            }
            
            // Добавляем дату возврата, если она заполнена
            if (returnDate.trim()) {
                const formattedDate = formatDateToDDMMYYYY(returnDate);
                updateData.approximateDeliveryDate = formattedDate;
            }
            
            // Отправляем запрос на обновление статуса
            await ordersProxy.updateStatus(record.orderId, updateData);
            
            setReturnDialogOpen(false);
            setTrackNumber('');
            setProviderOrderId('');
            setReturnPvzCode('');
            setReturnPvzCity('');
            setReturnPvzAddress('');
            setReturnPvzWorkingHours('');
            setReturnPvzLatitude('');
            setReturnPvzLongitude('');
            setReturnDate('');
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса на возврат:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handlePvzSubmit = async () => {
        try {
            // Подготавливаем данные для отправки
            const pvzData = {
                event: 'SET_RETURN_PVZ_INFO',
                code: pvzCode,
                city: pvzCity,
                address: pvzAddress,
                workTime: pvzWorkingHours,
                point: {
                    latitude: parseFloat(pvzLatitude),
                    longitude: parseFloat(pvzLongitude)
                }
            };
            
            // Отправляем запрос через sendDeliveryEvent
            await ordersProxy.sendDeliveryEvent(record.orderId, pvzData);
            
            setPvzDialogOpen(false);
            setPvzCode('');
            setPvzCity('');
            setPvzAddress('');
            setPvzWorkingHours('');
            setPvzLatitude('');
            setPvzLongitude('');
            
            // Обновляем страницу после успешного запроса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при обновлении информации о ПВЗ:', error);
            notify('Ошибка при обновлении информации о ПВЗ', { type: 'error' });
        }
    };

    const handleReturnPvzSubmit = () => {
        setReturnPvzDialogOpen(false);
        setReturnPvzCode('');
        setReturnPvzCity('');
        setReturnPvzAddress('');
        setReturnPvzWorkingHours('');
        setReturnPvzLatitude('');
        setReturnPvzLongitude('');
    };

    const handleArrivalDateSubmit = async () => {
        try {
            // Форматируем дату в формат dd.MM.yyyy
            const formattedDate = formatDateToDDMMYYYY(arrivalDate);
            
            await ordersProxy.sendDeliveryEvent(record.orderId, {
                event: 'SET_APPROXIMATE_DATE_FOR_RETURN_DELIVERY',
                date: formattedDate
            });
            
            setArrivalDateDialogOpen(false);
            setArrivalDate('');
            
            // Обновляем страницу после успешного запроса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при установке даты прибытия посылки:', error);
            notify('Ошибка при установке даты прибытия посылки', { type: 'error' });
        }
    };

    const handleDeliveryArrivalDateSubmit = async () => {
        try {
            // Форматируем дату в формат dd.MM.yyyy
            const formattedDate = formatDateToDDMMYYYY(deliveryArrivalDate);
            
            await ordersProxy.sendDeliveryEvent(record.orderId, {
                event: 'SET_APPROXIMATE_DATE_FOR_DIRECT_DELIVERY',
                date: formattedDate
            });
            
            setDeliveryArrivalDateDialogOpen(false);
            setDeliveryArrivalDate('');
            
            // Обновляем страницу после успешного запроса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при установке даты прибытия:', error);
            notify('Ошибка при установке даты прибытия', { type: 'error' });
        }
    };

    const handleInitiateReturnSubmit = () => {
        setInitiateReturnDialogOpen(false);
        setReturnTrackNumber('');
        setReturnProviderOrderId('');
    };

    // Обработчики для кнопок разработки
    const handleDevTempErrorSubmit = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'ERROR_WHEN_CREATING_DELIVERY_ORDER'
            });
            
            setDevTempErrorDialogOpen(false);
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handleDevFatalErrorSubmit = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'DELIVERY_ORDER_IS_INVALID'
            });
            
            setDevFatalErrorDialogOpen(false);
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handleRetryDeliveryOrderSubmit = async () => {
        if (!senderFullName.trim() || !senderPhone.trim()) {
            return;
        }

        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'CREATING_DELIVERY_ORDER',
                senderFullName: senderFullName.trim(),
                senderPhone: senderPhone.trim()
            });

            setRetryDeliveryOrderDialogOpen(false);
            setSenderFullName('');
            setSenderPhone('');
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при перезапросе заказа:', error);
            notify('Ошибка при перезапросе заказа', { type: 'error' });
        }
    };

    const handleDeliveryExpired = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'DELIVERY_EXPIRED'
            });
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handleInDelivery = () => {
        setDeliveryTimeDialogOpen(true);
    };

    const generateRandomPvzData = () => {
        const cities = ['Москва', 'Санкт-Петербург', 'Новосибирск', 'Екатеринбург', 'Казань'];
        const streets = ['Ленина', 'Пушкина', 'Гагарина', 'Мира', 'Советская'];
        const pvzCodes = ['PVZ001', 'PVZ002', 'PVZ003', 'PVZ004', 'PVZ005'];
        
        setPvzCode(pvzCodes[Math.floor(Math.random() * pvzCodes.length)]);
        setPvzCity(cities[Math.floor(Math.random() * cities.length)]);
        setPvzAddress(`ул. ${streets[Math.floor(Math.random() * streets.length)]}, ${Math.floor(Math.random() * 100) + 1}`);
        setPvzWorkingHours('09:00-18:00');
        setPvzLatitude((55 + Math.random() * 10).toFixed(6));
        setPvzLongitude((37 + Math.random() * 10).toFixed(6));
        

    };

    const generateRandomReturnPvzData = () => {
        const cities = ['Москва', 'Санкт-Петербург', 'Новосибирск', 'Екатеринбург', 'Казань'];
        const streets = ['Ленина', 'Пушкина', 'Гагарина', 'Мира', 'Советская'];
        const pvzCodes = ['PVZ001', 'PVZ002', 'PVZ003', 'PVZ004', 'PVZ005'];
        
        setReturnPvzCode(pvzCodes[Math.floor(Math.random() * pvzCodes.length)]);
        setReturnPvzCity(cities[Math.floor(Math.random() * cities.length)]);
        setReturnPvzAddress(`ул. ${streets[Math.floor(Math.random() * streets.length)]}, ${Math.floor(Math.random() * 100) + 1}`);
        setReturnPvzWorkingHours('09:00-18:00');
        setReturnPvzLatitude((55 + Math.random() * 10).toFixed(6));
        setReturnPvzLongitude((37 + Math.random() * 10).toFixed(6));
        

    };

    const generateRandomDate = () => {
        // Генерируем дату в диапазоне от сегодня до +30 дней
        const today = new Date();
        const futureDate = new Date();
        futureDate.setDate(today.getDate() + Math.floor(Math.random() * 30) + 1);
        
        // Форматируем дату в формат YYYY-MM-DD для input type="date"
        const year = futureDate.getFullYear();
        const month = String(futureDate.getMonth() + 1).padStart(2, '0');
        const day = String(futureDate.getDate()).padStart(2, '0');
        
        const randomDate = `${year}-${month}-${day}`;
        setArrivalDate(randomDate);
        

    };

    const generateRandomDeliveryDate = () => {
        // Генерируем дату в диапазоне от сегодня до +30 дней
        const today = new Date();
        const futureDate = new Date();
        futureDate.setDate(today.getDate() + Math.floor(Math.random() * 30) + 1);
        
        // Форматируем дату в формат YYYY-MM-DD для input type="date"
        const year = futureDate.getFullYear();
        const month = String(futureDate.getMonth() + 1).padStart(2, '0');
        const day = String(futureDate.getDate()).padStart(2, '0');
        
        const randomDate = `${year}-${month}-${day}`;
        setDeliveryArrivalDate(randomDate);
    };

    const generateRandomReturnData = (useReturnDialog = false) => {
        // Генерируем трек-номер из 10 цифр
        const trackNumber = Math.floor(Math.random() * 9000000000) + 1000000000;
        
        // Генерируем UUID
        const generateUUID = () => {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        };
        
        if (useReturnDialog) {
            // Для диалога возврата используем trackNumber и providerOrderId
            setTrackNumber(trackNumber.toString());
            setProviderOrderId(generateUUID());
        } else {
            // Для других случаев используем returnTrackNumber и returnProviderOrderId
            setReturnTrackNumber(trackNumber.toString());
            setReturnProviderOrderId(generateUUID());
        }
    };

    const generateRandomDeliveryTime = () => {
        // Генерируем дату в диапазоне от сегодня до +30 дней
        const today = new Date();
        const futureDate = new Date();
        futureDate.setDate(today.getDate() + Math.floor(Math.random() * 30) + 1);
        
        // Форматируем дату в формат YYYY-MM-DD для HTML date input
        const year = futureDate.getFullYear();
        const month = String(futureDate.getMonth() + 1).padStart(2, '0');
        const day = String(futureDate.getDate()).padStart(2, '0');
        
        const randomDate = `${year}-${month}-${day}`;
        setApproximateDeliveryDate(randomDate);
    };

    const generateRandomReturnDate = () => {
        // Генерируем дату в диапазоне от сегодня до +30 дней
        const today = new Date();
        const futureDate = new Date();
        futureDate.setDate(today.getDate() + Math.floor(Math.random() * 30) + 1);
        
        // Форматируем дату в формат YYYY-MM-DD для HTML date input
        const year = futureDate.getFullYear();
        const month = String(futureDate.getMonth() + 1).padStart(2, '0');
        const day = String(futureDate.getDate()).padStart(2, '0');
        
        const randomDate = `${year}-${month}-${day}`;
        setReturnDate(randomDate);
    };



    // Функция для форматирования даты из YYYY-MM-DD в dd.MM.yyyy
    const formatDateToDDMMYYYY = (dateString: string): string => {
        if (!dateString) return '';
        
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}.${month}.${year}`;
    };

    // Функция для валидации даты
    const isValidDate = (dateString: string) => {
        if (!dateString) return true; // Пустая дата разрешена
        
        // Проверяем формат YYYY-MM-DD
        const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!dateMatch) return false;
        
        const year = parseInt(dateMatch[1]);
        const month = parseInt(dateMatch[2]);
        const day = parseInt(dateMatch[3]);
        
        // Проверяем диапазоны
        if (year < 1900 || year > 2100) return false;
        if (month < 1 || month > 12) return false;
        if (day < 1 || day > 31) return false;
        
        // Проверяем корректность даты (например, 30 февраля не существует)
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year && 
               date.getMonth() === month - 1 && 
               date.getDate() === day;
    };

    const handleDeliveryTimeSubmit = async () => {
        try {
            console.log('Перевод в статус "Доставляется" с датой:', approximateDeliveryDate);
            
            const updateData: any = {
                status: 'IN_DELIVERY'
            };
            
            // Добавляем дату доставки только если она заполнена
            if (approximateDeliveryDate.trim()) {
                // Форматируем дату из YYYY-MM-DD в dd.MM.yyyy для API
                const dateParts = approximateDeliveryDate.split('-');
                if (dateParts.length === 3) {
                    const year = dateParts[0];
                    const month = dateParts[1];
                    const day = dateParts[2];
                    updateData.approximateDeliveryDate = `${day}.${month}.${year}`;
                }
            }
            
            await ordersProxy.updateStatus(record.orderId, updateData);
            
            setDeliveryTimeDialogOpen(false);
            setApproximateDeliveryDate('');
            notify('Статус изменен на "Доставляется"', { type: 'success' });
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const generateRandomShipmentData = () => {
        // Генерируем трек-номер из 10 цифр
        const trackNumber = Math.floor(Math.random() * 9000000000) + 1000000000;
        
        // Генерируем UUID
        const generateUUID = () => {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        };
        
        setTrackNumber(trackNumber.toString());
        setProviderOrderId(generateUUID());
    };

    const handleDeliveredToPvz = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'IN_DELIVERY_POINT'
            });
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handleReceived = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'RECEIVED'
            });
            
            setReceivedConfirmationDialogOpen(false);
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handleCancelledByExpiredPicking = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'CANCELLED_BY_EXPIRED_PICKING'
            });
            
            setCancelledByExpiredPickingDialogOpen(false);
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handleCancelledAfterDelivered = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'CANCELLED_AFTER_DELIVERED_BY_CUSTOMER'
            });
            
            setCancelledAfterDeliveredDialogOpen(false);
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handleDeliveredBack = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'ORDER_RETURNED_TO_SELLER_POINT'
            });
            
            setDeliveredBackConfirmationDialogOpen(false);
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    const handleReturnedOrderPickedBySeller = async () => {
        try {
            await ordersProxy.updateStatus(record.orderId, {
                status: 'RETURNED_ORDER_WAS_PICKED_BY_SELLER'
            });
            
            // Обновляем страницу для отображения нового статуса
            window.location.reload();
            
        } catch (error) {
            console.error('Ошибка при изменении статуса:', error);
            notify('Ошибка при изменении статуса заказа', { type: 'error' });
        }
    };

    // Получение цвета для статуса из централизованной мапы
    const getStatusColor = (status: ProgressOrderStatus) => {
        return statusBanners[status]?.color || 'default';
    };

    // Мапа состояний доставки
    const deliveryStateMap = {
        'SUCCESS': {
            label: 'Создан',
            color: 'info' as const
        },
        'IN_PROGRESS': {
            label: 'Выполняется',
            color: 'primary' as const
        },
        'CANCELLED_BY_OUR_SYSTEM': {
            label: 'Отменен нашей системой',
            color: 'error' as const
        },
        'MANUAL_CANCELLED_BEFORE_PROGRESS': {
            label: 'Отменен пользователем до отправки',
            color: 'warning' as const
        },
        'MANUAL_CANCELLED_IN_PROGRESS': {
            label: 'Отменен пользователем во время доставки',
            color: 'warning' as const
        },
        'WAITING_RETURN_ORDER_INFO': {
            label: 'Ожидание информации о возврате',
            color: 'info' as const
        },
        'DONE': {
            label: 'Выполнен',
            color: 'success' as const
        },
        'RETURN': {
            label: 'Создан возврат',
            color: 'error' as const
        }
    };

    const getDeliveryStateColor = (state: string) => {
        return deliveryStateMap[state as keyof typeof deliveryStateMap]?.color || 'default';
    };

    const getDeliveryStateLabel = (state: string) => {
        return deliveryStateMap[state as keyof typeof deliveryStateMap]?.label || state;
    };

    // Мапа состояний обратной доставки
    const returnDeliveryStateMap = {
        'SUCCESS': {
            label: 'Успешно создан',
            color: 'success' as const
        },
        'IN_PROGRESS': {
            label: 'В пути к продавцу',
            color: 'info' as const
        },
        'WAITING_PICKING': {
            label: 'Ожидает приема продавцом',
            color: 'warning' as const
        },
        'DONE': {
            label: 'Выполнен/закрыт',
            color: 'success' as const
        }
    };

    const getReturnDeliveryStateColor = (state: string) => {
        return returnDeliveryStateMap[state as keyof typeof returnDeliveryStateMap]?.color || 'default';
    };

    const getReturnDeliveryStateLabel = (state: string) => {
        return returnDeliveryStateMap[state as keyof typeof returnDeliveryStateMap]?.label || state;
    };

    const getStatusLabel = (status: ProgressOrderStatus) => {
        return statusBanners[status]?.label || status;
    };
    const plannedPayoutDate = getPlannedPayoutDate(paymentInfo?.finishedAt);
    const originalPrice = roundUpTo2(normalizeNumber(record?.priceInfo?.originalPrice));
    const feeAmount = roundUpTo2(normalizeNumber(record?.priceInfo?.fee?.amount));
    const loyaltyUsed = roundUpTo2(normalizeNumber(record?.priceInfo?.loyalty?.used));
    const deliveryPrice = roundUpTo2(normalizeNumber(record?.priceInfo?.deliveryPrice));

    const feeAfterBonus = roundUpTo2(Math.max(1, feeAmount - loyaltyUsed));
    const usedForFee = roundUpTo2(Math.max(0, feeAmount - feeAfterBonus));
    const usedRemainAfterFee = roundUpTo2(Math.max(0, loyaltyUsed - usedForFee));

    const usedForDelivery = roundUpTo2(Math.min(usedRemainAfterFee, deliveryPrice));
    const deliveryAfterBonus = roundUpTo2(Math.max(0, deliveryPrice - usedForDelivery));
    const usedRemainAfterDelivery = roundUpTo2(Math.max(0, usedRemainAfterFee - usedForDelivery));

    const payoutDeficit = roundUpTo2(Math.min(usedRemainAfterDelivery, originalPrice));
    const sellerReceipt = receiptContext?.receipt || null;
    const sellerReceiptStatus = sellerReceipt?.status?.trim().toUpperCase() || '';
    const receiptOutcomeColor = (outcome: SellerReceiptCreationAuditItem['outcome']) => {
        if (outcome === 'SUCCESS') {
            return 'success' as const;
        }
        if (outcome === 'ERROR') {
            return 'error' as const;
        }
        return 'info' as const;
    };

    return (
                <>
            {/* Универсальный баннер для статусов */}
            {statusBanners[record.status as keyof typeof statusBanners] && (
                <Box sx={{ 
                    mt: 2,
                    mb: 1,
                    px: 2
                }}>
                    <Alert 
                        severity={statusBanners[record.status as keyof typeof statusBanners].severity}
                        sx={{ 
                            '& .MuiAlert-message': {
                                fontSize: '1rem'
                            }
                        }}
                    >
                        {statusBanners[record.status as keyof typeof statusBanners].text}
                    </Alert>
                </Box>
            )}

            <Show title={`Заказ #${record.serialNumber}`}>
                <SimpleShowLayout>
                    <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Основная информация</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">ID заказа</Typography>
                                <Typography variant="body1">{record.orderId}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Номер заказа</Typography>
                                <Typography variant="body1">{record.serialNumber}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Статус</Typography>
                                <Chip
                                    label={getStatusLabel(record.status)}
                                    color={getStatusColor(record.status)}
                                    variant="outlined"
                                    size="medium"
                                    sx={{ fontSize: '1rem', padding: '8px 16px' }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Дата создания</Typography>
                                <Typography variant="body1">
                                    {new Date(record.creationDate).toLocaleString('ru-RU')}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Последнее обновление</Typography>
                                <Typography variant="body1">
                                    {new Date(record.lastUpdate).toLocaleString('ru-RU')}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Информация о товаре</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">ID объявления</Typography>
                                <Typography variant="body1">{record.adInfo?.adId}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Бренд</Typography>
                                <Typography variant="body1">{record.adInfo?.brand}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Модель</Typography>
                                <Typography variant="body1">{record.adInfo?.model}</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Участники заказа</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>Продавец</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Avatar 
                                        src={record.usersInfo?.seller?.avatarUrl || undefined}
                                        alt={record.usersInfo?.seller?.name}
                                        sx={{ width: 56, height: 56 }}
                                    >
                                        {record.usersInfo?.seller?.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Имя</Typography>
                                            <Typography variant="body1">{record.usersInfo?.seller?.name}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">ID</Typography>
                                            <Typography variant="body1">
                                                {record.usersInfo?.seller?.id ? (
                                                    canOpenUserShow ? (
                                                        <MuiLink
                                                            href={getPlatformUserShowUrl(record.usersInfo.seller.id)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            underline="hover"
                                                            sx={{ fontSize: 'inherit' }}
                                                        >
                                                            {record.usersInfo.seller.id}
                                                        </MuiLink>
                                                    ) : record.usersInfo.seller.id
                                                ) : '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" gutterBottom>Покупатель</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Avatar 
                                        src={record.usersInfo?.customer?.avatarUrl || undefined}
                                        alt={record.usersInfo?.customer?.name}
                                        sx={{ width: 56, height: 56 }}
                                    >
                                        {record.usersInfo?.customer?.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Имя</Typography>
                                            <Typography variant="body1">{record.usersInfo?.customer?.name}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">ID</Typography>
                                            <Typography variant="body1">
                                                {record.usersInfo?.customer?.id ? (
                                                    canOpenUserShow ? (
                                                        <MuiLink
                                                            href={getPlatformUserShowUrl(record.usersInfo.customer.id)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            underline="hover"
                                                            sx={{ fontSize: 'inherit' }}
                                                        >
                                                            {record.usersInfo.customer.id}
                                                        </MuiLink>
                                                    ) : record.usersInfo.customer.id
                                                ) : '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Ценовая информация</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Цена объявления</Typography>
                                <Typography variant="body1">
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(record.priceInfo?.adPrice || 0)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Исходная цена</Typography>
                                <Typography variant="body1">
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(record.priceInfo?.originalPrice || 0)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Итоговая цена</Typography>
                                <Typography variant="body1">
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(record.priceInfo?.totalPrice || 0)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Стоимость доставки</Typography>
                                <Typography variant="body1">
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(record.priceInfo?.deliveryPrice || 0)}
                                    {` (${formatCurrencyValue(deliveryAfterBonus)}, вычлось бонусов: ${formatCurrencyValue(usedForDelivery)})`}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Комиссия</Typography>
                                <Typography variant="body1">
                                    {new Intl.NumberFormat('ru-RU', {
                                        style: 'currency',
                                        currency: 'RUB',
                                    }).format(record.priceInfo?.fee?.amount || 0)}
                                    {` (${formatCurrencyValue(feeAfterBonus)}, вычлось бонусов: ${formatCurrencyValue(usedForFee)})`}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Процент комиссии</Typography>
                                <Typography variant="body1">
                                    {record.priceInfo?.fee?.percent || 0}%
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Начислено бонусов</Typography>
                                <Typography variant="body1">
                                    {record.priceInfo?.loyalty?.earn ?? 0}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Списано бонусов</Typography>
                                <Typography variant="body1">
                                    {record.priceInfo?.loyalty?.used ?? 0}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Дефицит выплаты</Typography>
                                <Typography variant="body1">
                                    {formatCurrencyValue(payoutDeficit)}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {canViewOrderPaymentInfo && (paymentInfoLoading || paymentInfo || paymentInfoError) && (
                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Выплата</Typography>
                            {paymentInfoLoading ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                    {[...Array(6)].map((_, index) => (
                                        <Box key={index}>
                                            <Skeleton variant="text" width="40%" height={20} />
                                            <Skeleton variant="text" width="85%" height={24} />
                                        </Box>
                                    ))}
                                </Box>
                            ) : paymentInfoError ? (
                                <Alert severity="error">{paymentInfoError}</Alert>
                            ) : paymentInfo ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Внутренний ID оплаты</Typography>
                                        <Typography variant="body1">{formatOptionalValue(paymentInfo.id)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">ID оплаты (ypmn)</Typography>
                                        <Typography variant="body1">{formatOptionalValue(paymentInfo.ypmnId)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Статус</Typography>
                                        <Typography variant="body1">{formatOrderPayoutState(paymentInfo.state)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Сумма оплаты</Typography>
                                        <Typography variant="body1">{formatPayoutAmount(paymentInfo.amountSeller)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">ID заказа</Typography>
                                        <Typography variant="body1">{formatOptionalValue(paymentInfo.orderId)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Название айтема</Typography>
                                        <Typography variant="body1">{formatOptionalValue(paymentInfo.adTitleInfo)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Выплата заблокирована</Typography>
                                        <Typography variant="body1">{paymentInfo.payoutBlocked ? 'Да' : 'Нет'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Дата завершения заказа</Typography>
                                        <Typography variant="body1">{formatDateTimeValue(paymentInfo.finishedAt)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Дата выплаты</Typography>
                                        <Typography variant="body1">{formatDateTimeValue(paymentInfo.paidAt)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Ошибка выплаты</Typography>
                                        <Typography variant="body1">{formatOrderPayoutErrorState(paymentInfo.payoutErrorState)}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Метод выплаты</Typography>
                                        <Typography variant="body1">{formatOptionalValue(paymentInfo.payoutMethod)}</Typography>
                                    </Box>
                                    {paymentInfo.finishedAt && (
                                        <>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Плановая дата выплаты</Typography>
                                                <Typography variant="body1">
                                                    {plannedPayoutDate ? plannedPayoutDate.toLocaleString('ru-RU') : 'Не удалось рассчитать'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ gridColumn: '1 / -1' }}>
                                                <Alert severity="info">
                                                    Выплата будет через 24 часа после завершения заказа.
                                                </Alert>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            ) : null}
                        </CardContent>
                    </Card>
                )}

                <Accordion
                    expanded={receiptExpanded}
                    onChange={handleReceiptAccordionChange}
                    sx={{ mt: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="h6">Чек</Typography>
                            {sellerReceiptStatus === 'FISCALIZED' && (
                                <Chip label="Фискализирован" color="success" size="small" />
                            )}
                            {sellerReceiptStatus === 'PENDING' && (
                                <Chip label="Формируется" color="warning" size="small" />
                            )}
                            {receiptPollingActive && <CircularProgress size={18} />}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        {receiptLoading && !receiptContext ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                                {[...Array(6)].map((_, index) => (
                                    <Box key={index}>
                                        <Skeleton variant="text" width="45%" height={20} />
                                        <Skeleton variant="text" width="85%" height={24} />
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {receiptError && (
                                    <Alert
                                        severity={receiptPollingActive ? 'warning' : 'error'}
                                        action={receiptErrorSource === 'load' ? (
                                            <Button
                                                color="inherit"
                                                size="small"
                                                startIcon={<RefreshIcon />}
                                                onClick={handleRefreshReceipt}
                                                disabled={receiptLoading}
                                            >
                                                Повторить
                                            </Button>
                                        ) : undefined}
                                    >
                                        {receiptError}
                                    </Alert>
                                )}

                                {receiptContext?.eligibility && receiptContext.eligibility !== 'ELIGIBLE' ? (
                                    <Alert severity="info">
                                        Чек сформируется автоматически, когда заказ будет получен и выплата успешно выполнена.
                                    </Alert>
                                ) : receiptContext?.eligibility === 'ELIGIBLE'
                                    && !sellerReceipt
                                    && (!receiptError || receiptErrorSource === 'create') ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
                                        <Alert severity="warning" sx={{ width: '100%' }}>Чек не найден</Alert>
                                        {canCreateSellerReceipt ? (
                                            <Button
                                                variant="contained"
                                                onClick={() => void handleCreateReceipt()}
                                                disabled={receiptCreating}
                                                startIcon={receiptCreating ? <CircularProgress size={18} color="inherit" /> : undefined}
                                            >
                                                {receiptCreating ? 'Формирование…' : 'Сформировать чек'}
                                            </Button>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Ручное формирование доступно только manager-admin и moderator-admin.
                                            </Typography>
                                        )}
                                    </Box>
                                ) : sellerReceipt ? (
                                    <>
                                        {sellerReceiptStatus === 'PENDING' && (
                                            <Alert severity="info">
                                                Чек формируется. Информация обновляется автоматически каждые 5 секунд.
                                            </Alert>
                                        )}
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                                                gap: 2
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">ID чека</Typography>
                                                <Typography variant="body1">{formatOptionalValue(sellerReceipt.id)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Внутренний ID оплаты</Typography>
                                                <Typography variant="body1">{formatOptionalValue(sellerReceipt.orderPaymentId)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">ID заказа</Typography>
                                                <Typography variant="body1">{formatOptionalValue(sellerReceipt.orderId)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Статус</Typography>
                                                <Chip
                                                    label={sellerReceiptStatus === 'FISCALIZED' ? 'Фискализирован' : 'Формируется'}
                                                    color={sellerReceiptStatus === 'FISCALIZED' ? 'success' : 'warning'}
                                                    size="small"
                                                />
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">ID чека провайдера</Typography>
                                                <Typography variant="body1">{formatOptionalValue(sellerReceipt.providerReceiptId)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Сумма</Typography>
                                                <Typography variant="body1">
                                                    {sellerReceipt.amount ? `${sellerReceipt.amount} ₽` : 'Не указано'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Время регистрации</Typography>
                                                <Typography variant="body1">{formatDateTimeValue(sellerReceipt.regTime)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Фискальный признак</Typography>
                                                <Typography variant="body1">
                                                    {sellerReceipt.fiscalSign ?? 'Не указано'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Номер фискального документа</Typography>
                                                <Typography variant="body1">
                                                    {sellerReceipt.fiscalNum ?? 'Не указано'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Создан</Typography>
                                                <Typography variant="body1">{formatDateTimeValue(sellerReceipt.createdAt)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Обновлён</Typography>
                                                <Typography variant="body1">{formatDateTimeValue(sellerReceipt.updatedAt)}</Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Фискализирован</Typography>
                                                <Typography variant="body1">{formatDateTimeValue(sellerReceipt.fiscalizedAt)}</Typography>
                                            </Box>
                                            <Box sx={{ gridColumn: '1 / -1' }}>
                                                <Typography variant="body2" color="text.secondary">Ссылка на чек</Typography>
                                                {sellerReceipt.receiptUrl?.startsWith('https://') ? (
                                                    <MuiLink
                                                        href={sellerReceipt.receiptUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                    >
                                                        Открыть фискальный чек
                                                    </MuiLink>
                                                ) : (
                                                    <Typography variant="body1">
                                                        {formatOptionalValue(sellerReceipt.receiptUrl)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </>
                                ) : !receiptError ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Раскройте раздел, чтобы загрузить информацию о чеке.
                                    </Typography>
                                ) : null}

                                <Divider />
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                                        <Typography variant="subtitle1">Аудит формирования</Typography>
                                        <Button
                                            size="small"
                                            startIcon={<RefreshIcon />}
                                            onClick={() => void loadReceiptAudit()}
                                            disabled={receiptAuditLoading}
                                        >
                                            Обновить
                                        </Button>
                                    </Box>
                                    {receiptAuditLoading && receiptAudit.length === 0 ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : receiptAuditError ? (
                                        <Alert severity="error">{receiptAuditError}</Alert>
                                    ) : receiptAudit.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Попыток ручного формирования нет.
                                        </Typography>
                                    ) : (
                                        <List disablePadding>
                                            {receiptAudit.map((item, index) => {
                                                const actorName = item.actor.fullName || item.actor.login || item.actor.id || 'Неизвестный пользователь';
                                                return (
                                                    <React.Fragment key={item.id || `${item.initiatedAt}-${index}`}>
                                                        {index > 0 && <Divider component="li" />}
                                                        <ListItem disableGutters alignItems="flex-start">
                                                            <ListItemText
                                                                primary={(
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                        <Chip
                                                                            label={getReceiptOutcomeLabel(item.outcome)}
                                                                            color={receiptOutcomeColor(item.outcome)}
                                                                            size="small"
                                                                        />
                                                                        <Typography variant="body2">
                                                                            {formatDateTimeValue(item.initiatedAt)}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {actorName} ({item.actor.role || 'роль не указана'})
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                                secondary={(
                                                                    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 0.75 }}>
                                                                        <Typography component="span" variant="body2" color="text.secondary">
                                                                            HTTP: {item.httpStatus ?? '—'}; ID оплаты: {item.paymentId || '—'}
                                                                            {item.receiptStatus ? `; статус чека: ${item.receiptStatus}` : ''}
                                                                        </Typography>
                                                                        {item.completedAt && (
                                                                            <Typography component="span" variant="body2" color="text.secondary">
                                                                                Завершено: {formatDateTimeValue(item.completedAt)}
                                                                            </Typography>
                                                                        )}
                                                                        {item.outcome === 'ERROR' && (
                                                                            <Typography component="span" variant="body2" color="error.main">
                                                                                Ошибка{item.errorId ? ` ${item.errorId}` : ''}: {item.errorMessage || 'Неизвестная ошибка'}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                )}
                                                            />
                                                        </ListItem>
                                                    </React.Fragment>
                                                );
                                            })}
                                        </List>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Информация о получателе</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">ФИО</Typography>
                                <Typography variant="body1">{record.receiverInfo?.fullName}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Телефон</Typography>
                                <Typography variant="body1">{record.receiverInfo?.phoneNumber}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Email</Typography>
                                <Typography variant="body1">{record.receiverInfo?.email}</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Информация об отправителе (продавец)</Typography>
                        {record.senderInfo ? (
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">ФИО</Typography>
                                    <Typography variant="body1">{record.senderInfo.fullName}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Телефон</Typography>
                                    <Typography variant="body1">{record.senderInfo.phoneNumber}</Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Информации об отправителе нет
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Информация о доставке</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Провайдер</Typography>
                                <Typography variant="body1">{record.deliveryInfo?.provider || 'Не указан'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Откуда</Typography>
                                <Typography variant="body1">{record.deliveryInfo?.from || 'Не указан'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Куда</Typography>
                                <Typography variant="body1">{record.deliveryInfo?.to || 'Не указан'}</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Информация о прямой доставке</Typography>
                        {record.providerDirectDeliveryInfo ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">ID заказа доставки</Typography>
                                        <Typography variant="body1">{record.providerDirectDeliveryInfo.providerDeliveryOrderId}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Трек номер</Typography>
                                        <Typography variant="body1">{record.providerDirectDeliveryInfo.trackNumber || 'Не указан'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Состояние</Typography>
                                        <Chip
                                            label={getDeliveryStateLabel(record.providerDirectDeliveryInfo.state)}
                                            color={getDeliveryStateColor(record.providerDirectDeliveryInfo.state)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Приблизительная дата доставки</Typography>
                                        {record.providerDirectDeliveryInfo.approximateDeliveryDate ? (
                                            <Box>
                                                <Typography variant="body1">
                                                    {record.providerDirectDeliveryInfo.approximateDeliveryDate.date}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                                    Последнее обновление: {new Date(record.providerDirectDeliveryInfo.approximateDeliveryDate.lastUpdatedDate).toLocaleString('ru-RU')}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body1">Не указана</Typography>
                                        )}
                                    </Box>
                                </Box>
                                
                                {record.providerDirectDeliveryInfo.history && record.providerDirectDeliveryInfo.history.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>История доставки</Typography>
                                        <List>
                                            {record.providerDirectDeliveryInfo.history.map((historyItem: any, index: number) => (
                                                <ListItem key={index} sx={{ px: 0 }}>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Chip
                                                                    label={historyItem.orderStatus}
                                                                    color="default"
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {new Date(historyItem.date).toLocaleString('ru-RU')}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Информации о прямой доставке нет
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Информация об обратной доставке</Typography>
                        {record.providerReturnDeliveryInfo ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">ID заказа возврата</Typography>
                                        <Typography variant="body1">{record.providerReturnDeliveryInfo.id}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">ID заказа доставки</Typography>
                                        <Typography variant="body1">{record.providerReturnDeliveryInfo.providerDeliveryOrderId}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Трек номер</Typography>
                                        <Typography variant="body1">{record.providerReturnDeliveryInfo.trackNumber || 'Не указан'}</Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Состояние</Typography>
                                        <Chip
                                            label={getReturnDeliveryStateLabel(record.providerReturnDeliveryInfo.state)}
                                            color={getReturnDeliveryStateColor(record.providerReturnDeliveryInfo.state)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Приблизительная дата доставки</Typography>
                                        {record.providerReturnDeliveryInfo.approximateDeliveryDate ? (
                                            <Box>
                                                <Typography variant="body1">
                                                    {record.providerReturnDeliveryInfo.approximateDeliveryDate.date}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                                    Последнее обновление: {new Date(record.providerReturnDeliveryInfo.approximateDeliveryDate.lastUpdatedDate).toLocaleString('ru-RU')}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body1">Не указана</Typography>
                                        )}
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Информация об обратном ПВЗ</Typography>
                                    {record.providerReturnDeliveryInfo.pvzInfo ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">Код ПВЗ</Typography>
                                                    <Typography variant="body1">{record.providerReturnDeliveryInfo.pvzInfo.code}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">Полный адрес</Typography>
                                                    <Typography variant="body1">{record.providerReturnDeliveryInfo.pvzInfo.fullAddress}</Typography>
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">Последнее обновление информации</Typography>
                                                <Typography variant="body1">
                                                    {new Date(record.providerReturnDeliveryInfo.pvzInfo.lastUpdatedDate).toLocaleString('ru-RU')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            Нет данных
                                        </Typography>
                                    )}
                                </Box>
                                
                                {record.providerReturnDeliveryInfo.history && record.providerReturnDeliveryInfo.history.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>История возврата</Typography>
                                        <List>
                                            {record.providerReturnDeliveryInfo.history.map((historyItem: any, index: number) => (
                                                <ListItem key={index} sx={{ px: 0 }}>
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Chip
                                                                    label={getReturnDeliveryStateLabel(historyItem.orderStatus)}
                                                                    color={getReturnDeliveryStateColor(historyItem.orderStatus)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {new Date(historyItem.date).toLocaleString('ru-RU')}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Информации об обратной доставке нет
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>История статусов</Typography>
                        {record.statusesHistory && record.statusesHistory.length > 0 ? (
                            <List>
                                {record.statusesHistory.map((statusItem: any, index: number) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Chip
                                                        label={getStatusLabel(statusItem.status)}
                                                        color={getStatusColor(statusItem.status)}
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {new Date(statusItem.date).toLocaleString('ru-RU')}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                История статусов недоступна
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Диалоговое окно для изменения статуса на "Ожидает отправки" */}
                <Dialog open={shipmentDialogOpen} onClose={() => setShipmentDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Перевести в статус "Ожидает отправки"</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="Трек-номер в транспортной компании"
                                value={trackNumber}
                                onChange={(e) => setTrackNumber(e.target.value)}
                                fullWidth
                                variant="outlined"
                            />
                            <TextField
                                label="Идентификатор заказа транспортной компании"
                                value={providerOrderId}
                                onChange={(e) => setProviderOrderId(e.target.value)}
                                fullWidth
                                variant="outlined"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {process.env.NODE_ENV === 'development' && (
                            <Button 
                                onClick={generateRandomShipmentData} 
                                variant="outlined" 
                                color="secondary"
                                startIcon={<AutoAwesomeIcon />}
                            >
                                Сгенерировать
                            </Button>
                        )}
                        <Button onClick={() => setShipmentDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleShipmentSubmit} 
                            variant="contained" 
                            color="primary"
                            disabled={!trackNumber.trim() || !providerOrderId.trim() || isSubmitting}
                        >
                            {isSubmitting ? 'Отправка...' : 'Сменить статус'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалоговое окно для изменения статуса на "Возврат" */}
                <Dialog open={returnDialogOpen} onClose={() => setReturnDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Перевести в статус "Возврат"</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                            {/* Группа 1: Трек номер и идентификатор (обязательные) */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                                        * Трек номер и идентификатор (обязательные)
                                    </Typography>
                                    {process.env.NODE_ENV === 'development' && (
                                        <Button 
                                            onClick={() => generateRandomReturnData(true)} 
                                            variant="outlined" 
                                            color="secondary"
                                            size="small"
                                            startIcon={<AutoAwesomeIcon />}
                                        >
                                            Сгенерировать
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        label="Трек-номер в транспортной системе"
                                        value={trackNumber}
                                        onChange={(e) => setTrackNumber(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        required
                                    />
                                    <TextField
                                        label="Идентификатор заказа из транспортной системы"
                                        value={providerOrderId}
                                        onChange={(e) => setProviderOrderId(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        required
                                    />
                                </Box>
                            </Box>

                            {/* Группа 2: Информация о ПВЗ (необязательные, но если заполняется одно - заполнять все) */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Информация о ПВЗ (необязательные)
                                    </Typography>
                                    {process.env.NODE_ENV === 'development' && (
                                        <Button 
                                            onClick={generateRandomReturnPvzData} 
                                            variant="outlined" 
                                            color="secondary"
                                            size="small"
                                            startIcon={<AutoAwesomeIcon />}
                                        >
                                            Сгенерировать
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                        <TextField
                                            label="Код ПВЗ"
                                            value={returnPvzCode}
                                            onChange={(e) => setReturnPvzCode(e.target.value)}
                                            fullWidth
                                            variant="outlined"
                                        />
                                        <TextField
                                            label="Город"
                                            value={returnPvzCity}
                                            onChange={(e) => setReturnPvzCity(e.target.value)}
                                            fullWidth
                                            variant="outlined"
                                        />
                                    </Box>
                                    <TextField
                                        label="Адрес"
                                        value={returnPvzAddress}
                                        onChange={(e) => setReturnPvzAddress(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <TextField
                                        label="Время работы"
                                        value={returnPvzWorkingHours}
                                        onChange={(e) => setReturnPvzWorkingHours(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                    />
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                        <TextField
                                            label="Широта"
                                            value={returnPvzLatitude}
                                            onChange={(e) => setReturnPvzLatitude(e.target.value)}
                                            fullWidth
                                            variant="outlined"
                                            type="number"
                                        />
                                        <TextField
                                            label="Долгота"
                                            value={returnPvzLongitude}
                                            onChange={(e) => setReturnPvzLongitude(e.target.value)}
                                            fullWidth
                                            variant="outlined"
                                            type="number"
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Группа 3: Примерная дата возврата (необязательное) */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Примерная дата возврата (необязательное)
                                    </Typography>
                                    {process.env.NODE_ENV === 'development' && (
                                        <Button 
                                            onClick={generateRandomReturnDate} 
                                            variant="outlined" 
                                            color="secondary"
                                            size="small"
                                            startIcon={<AutoAwesomeIcon />}
                                        >
                                            Сгенерировать
                                        </Button>
                                    )}
                                </Box>
                                <TextField
                                    label="Дата возврата"
                                    value={returnDate}
                                    onChange={(e) => setReturnDate(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    type="date"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setReturnDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleReturnSubmit} 
                            variant="contained" 
                            color="warning"
                            disabled={!trackNumber.trim() || !providerOrderId.trim() || isPvzPartiallyFilled()}
                        >
                            Сменить статус
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалоговое окно для информации о ПВЗ */}
                <Dialog open={pvzDialogOpen} onClose={() => setPvzDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Информация о возвратном ПВЗ</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                <TextField
                                    label="Код ПВЗ"
                                    value={pvzCode}
                                    onChange={(e) => setPvzCode(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                                <TextField
                                    label="Город"
                                    value={pvzCity}
                                    onChange={(e) => setPvzCity(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Box>
                            <TextField
                                label="Адрес"
                                value={pvzAddress}
                                onChange={(e) => setPvzAddress(e.target.value)}
                                fullWidth
                                variant="outlined"
                            />
                            <TextField
                                label="Время работы"
                                value={pvzWorkingHours}
                                onChange={(e) => setPvzWorkingHours(e.target.value)}
                                fullWidth
                                variant="outlined"
                            />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                <TextField
                                    label="Широта"
                                    value={pvzLatitude}
                                    onChange={(e) => setPvzLatitude(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    type="number"
                                />
                                <TextField
                                    label="Долгота"
                                    value={pvzLongitude}
                                    onChange={(e) => setPvzLongitude(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    type="number"
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {process.env.NODE_ENV === 'development' && (
                            <Button 
                                onClick={generateRandomPvzData} 
                                variant="outlined" 
                                color="secondary"
                                startIcon={<AutoAwesomeIcon />}
                            >
                                Сгенерировать
                            </Button>
                        )}
                        <Button onClick={() => setPvzDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handlePvzSubmit} 
                            variant="contained" 
                            color="primary"
                            disabled={!pvzCode.trim() || !pvzCity.trim() || !pvzAddress.trim()}
                        >
                            Сохранить
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалоговое окно для даты прибытия */}
                <Dialog open={arrivalDateDialogOpen} onClose={() => setArrivalDateDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Дата примерного прибытия посылки</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="Дата прибытия"
                                value={arrivalDate}
                                onChange={(e) => setArrivalDate(e.target.value)}
                                fullWidth
                                variant="outlined"
                                type="date"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {process.env.NODE_ENV === 'development' && (
                            <Button 
                                onClick={generateRandomDate} 
                                variant="outlined" 
                                color="secondary"
                                startIcon={<AutoAwesomeIcon />}
                            >
                                Сгенерировать
                            </Button>
                        )}
                        <Button onClick={() => setArrivalDateDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleArrivalDateSubmit} 
                            variant="contained" 
                            color="primary"
                            disabled={!arrivalDate.trim()}
                        >
                            Выставить
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалоговое окно для информации о возвратном ПВЗ */}
                <Dialog open={returnPvzDialogOpen} onClose={() => setReturnPvzDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Информация о возвратном ПВЗ</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                <TextField
                                    label="Код ПВЗ"
                                    value={returnPvzCode}
                                    onChange={(e) => setReturnPvzCode(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                                <TextField
                                    label="Город"
                                    value={returnPvzCity}
                                    onChange={(e) => setReturnPvzCity(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Box>
                            <TextField
                                label="Адрес"
                                value={returnPvzAddress}
                                onChange={(e) => setReturnPvzAddress(e.target.value)}
                                fullWidth
                                variant="outlined"
                            />
                            <TextField
                                label="Время работы"
                                value={returnPvzWorkingHours}
                                onChange={(e) => setReturnPvzWorkingHours(e.target.value)}
                                fullWidth
                                variant="outlined"
                            />
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                <TextField
                                    label="Широта"
                                    value={returnPvzLatitude}
                                    onChange={(e) => setReturnPvzLatitude(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    type="number"
                                />
                                <TextField
                                    label="Долгота"
                                    value={returnPvzLongitude}
                                    onChange={(e) => setReturnPvzLongitude(e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    type="number"
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {process.env.NODE_ENV === 'development' && (
                            <Button 
                                onClick={generateRandomReturnPvzData} 
                                variant="outlined" 
                                color="secondary"
                                startIcon={<AutoAwesomeIcon />}
                            >
                                Сгенерировать
                            </Button>
                        )}
                        <Button onClick={() => setReturnPvzDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleReturnPvzSubmit} 
                            variant="contained" 
                            color="primary"
                            disabled={!returnPvzCode.trim() || !returnPvzCity.trim() || !returnPvzAddress.trim()}
                        >
                            Сохранить
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалоговое окно для даты прибытия доставки */}
                <Dialog open={deliveryArrivalDateDialogOpen} onClose={() => setDeliveryArrivalDateDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Дата примерного прибытия посылки в ПВЗ</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="Дата прибытия"
                                value={deliveryArrivalDate}
                                onChange={(e) => setDeliveryArrivalDate(e.target.value)}
                                fullWidth
                                variant="outlined"
                                type="date"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {process.env.NODE_ENV === 'development' && (
                            <Button 
                                onClick={generateRandomDeliveryDate} 
                                variant="outlined" 
                                color="secondary"
                                startIcon={<AutoAwesomeIcon />}
                            >
                                Сгенерировать
                            </Button>
                        )}
                        <Button onClick={() => setDeliveryArrivalDateDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleDeliveryArrivalDateSubmit} 
                            variant="contained" 
                            color="primary"
                            disabled={!deliveryArrivalDate.trim()}
                        >
                            Выставить
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалоговое окно для инициации возврата */}
                <Dialog open={initiateReturnDialogOpen} onClose={() => setInitiateReturnDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Инициировать возврат</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="Трек-номер в транспортной системе"
                                value={returnTrackNumber}
                                onChange={(e) => setReturnTrackNumber(e.target.value)}
                                fullWidth
                                variant="outlined"
                            />
                            <TextField
                                label="Идентификатор заказа из транспортной системы"
                                value={returnProviderOrderId}
                                onChange={(e) => setReturnProviderOrderId(e.target.value)}
                                fullWidth
                                variant="outlined"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {process.env.NODE_ENV === 'development' && (
                            <Button 
                                onClick={() => generateRandomReturnData(false)} 
                                variant="outlined" 
                                color="secondary"
                                startIcon={<AutoAwesomeIcon />}
                            >
                                Сгенерировать
                            </Button>
                        )}
                        <Button onClick={() => setInitiateReturnDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleInitiateReturnSubmit} 
                            variant="contained" 
                            color="warning"
                            disabled={!returnTrackNumber.trim() || !returnProviderOrderId.trim()}
                        >
                            Инициировать возврат
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог для временной ошибки разработки */}
                <Dialog open={devTempErrorDialogOpen} onClose={() => setDevTempErrorDialogOpen(false)}>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon color="warning" />
                            Перевести заказ во временную ошибку
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Вы уверены, что хотите перевести заказ во временную ошибку? 
                            Это действие доступно только в режиме разработки.
                        </Typography>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            ⚠️ Это действие симулирует временную ошибку при создании заказа доставки.
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDevTempErrorDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleDevTempErrorSubmit} 
                            variant="contained" 
                            color="warning"
                        >
                            Перевести во временную ошибку
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог для фатальной ошибки разработки */}
                <Dialog open={devFatalErrorDialogOpen} onClose={() => setDevFatalErrorDialogOpen(false)}>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorIcon color="error" />
                            Перевести заказ в фатальную ошибку
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Вы уверены, что хотите перевести заказ в фатальную ошибку? 
                            Это действие доступно только в режиме разработки.
                        </Typography>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            🚨 Это действие симулирует фатальную ошибку при создании заказа доставки.
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDevFatalErrorDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleDevFatalErrorSubmit} 
                            variant="contained" 
                            color="error"
                        >
                            Перевести в фатальную ошибку
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог для перезапроса заказа доставки */}
                <Dialog open={retryDeliveryOrderDialogOpen} onClose={() => setRetryDeliveryOrderDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {record.status === ProgressOrderStatus.REQUIRED_SENDER_INFORMATION
                            ? 'Перевести в новый статус'
                            : 'Перезапрос заказа доставки'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="ФИО отправителя"
                                value={senderFullName}
                                onChange={(e) => setSenderFullName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                required
                                autoFocus
                            />
                            <TextField
                                label="Номер телефона отправителя"
                                value={senderPhone}
                                onChange={handleSenderPhoneChange}
                                fullWidth
                                variant="outlined"
                                type="tel"
                                required
                                placeholder="+7XXXXXXXXXX"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {process.env.NODE_ENV === 'development' && (
                            <Button
                                onClick={generateRandomSenderInfo}
                                variant="outlined"
                                color="secondary"
                                startIcon={<AutoAwesomeIcon />}
                            >
                                Сгенерировать
                            </Button>
                        )}
                        <Button onClick={() => setRetryDeliveryOrderDialogOpen(false)}>Отмена</Button>
                        <Button
                            onClick={handleRetryDeliveryOrderSubmit}
                            variant="contained"
                            color="info"
                            disabled={!senderFullName.trim() || !senderPhone.trim()}
                        >
                            {record.status === ProgressOrderStatus.REQUIRED_SENDER_INFORMATION
                                ? 'Перевести'
                                : 'Перезапросить'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог для указания времени доставки */}
                <Dialog open={deliveryTimeDialogOpen} onClose={() => setDeliveryTimeDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Перевести в статус "Доставляется"</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label="Укажите приблизительное время доставки"
                                type="date"
                                value={approximateDeliveryDate}
                                onChange={(e) => setApproximateDeliveryDate(e.target.value)}
                                fullWidth
                                variant="outlined"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                helperText={
                                    approximateDeliveryDate && !isValidDate(approximateDeliveryDate)
                                        ? "Некорректная дата"
                                        : "Поле необязательное"
                                }
                                error={Boolean(approximateDeliveryDate && !isValidDate(approximateDeliveryDate))}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        {process.env.NODE_ENV === 'development' && (
                            <Button 
                                onClick={generateRandomDeliveryTime} 
                                variant="outlined" 
                                color="secondary"
                                startIcon={<AutoAwesomeIcon />}
                            >
                                Заполнить
                            </Button>
                        )}
                        <Button onClick={() => setDeliveryTimeDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleDeliveryTimeSubmit} 
                            variant="contained" 
                            color="primary"
                            disabled={!isValidDate(approximateDeliveryDate)}
                        >
                            Перевести в статус "Доставляется"
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог подтверждения получения заказа */}
                <Dialog open={receivedConfirmationDialogOpen} onClose={() => setReceivedConfirmationDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Подтверждение получения заказа</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Вы уверены, что хотите перевести заказ в статус "Получено"?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            После этого действия заказ будет считаться успешно доставленным и полученным покупателем.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setReceivedConfirmationDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleReceived} 
                            variant="contained" 
                            color="success"
                        >
                            Подтвердить получение
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог подтверждения отмены по истечении времени получения */}
                <Dialog open={cancelledByExpiredPickingDialogOpen} onClose={() => setCancelledByExpiredPickingDialogOpen(false)}>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon color="warning" />
                            Отменить заказ
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Вы уверены, что хотите отменить заказ по причине истечения времени получения?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            После этого действия заказ будет отменен и возвращен продавцу.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCancelledByExpiredPickingDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleCancelledByExpiredPicking} 
                            variant="contained" 
                            color="warning"
                        >
                            Отменить заказ
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог подтверждения отмены после доставки */}
                <Dialog open={cancelledAfterDeliveredDialogOpen} onClose={() => setCancelledAfterDeliveredDialogOpen(false)}>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ErrorIcon color="error" />
                            Отменить заказ
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Вы уверены, что хотите отменить заказ по причине отказа от получения?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            После этого действия заказ будет отменен и возвращен продавцу.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCancelledAfterDeliveredDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleCancelledAfterDelivered} 
                            variant="contained" 
                            color="error"
                        >
                            Отменить заказ
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог подтверждения доставки обратно */}
                <Dialog open={deliveredBackConfirmationDialogOpen} onClose={() => setDeliveredBackConfirmationDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Подтверждение доставки обратно</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Вы уверены, что хотите перевести заказ в статус "Доставлено обратно"?
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            После этого действия заказ будет считаться успешно доставленным обратно продавцу.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeliveredBackConfirmationDialogOpen(false)}>Отмена</Button>
                        <Button 
                            onClick={handleDeliveredBack} 
                            variant="contained" 
                            color="success"
                        >
                            Подтвердить доставку
                        </Button>
                    </DialogActions>
                </Dialog>
            </SimpleShowLayout>

        </Show>

        {/* Кнопки управления заказом - вне Show */}
        {record.status === ProgressOrderStatus.CREATING_DELIVERY_ORDER && (
            <>
                <Box sx={{ 
                    mt: 3, 
                    mb: 4,
                    display: 'flex', 
                    justifyContent: 'center',
                    px: 2,
                    py: 1.5
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<LocalShippingIcon />}
                        onClick={() => setShipmentDialogOpen(true)}
                        sx={{
                            px: 3,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            boxShadow: 2,
                            '&:hover': {
                                boxShadow: 4,
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        Перевести в статус "Ожидает отправки"
                    </Button>
                </Box>

                {/* Секция для разработки */}
                {process.env.NODE_ENV === 'development' && (
                    <Box sx={{ 
                        mt: 2, 
                        mb: 4,
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        px: 2,
                        py: 2,
                        border: '2px dashed #ff9800',
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 152, 0, 0.05)'
                    }}>
                        <Typography variant="h6" color="warning.main" sx={{ fontWeight: 600 }}>
                            🛠️ Раздел для разработчиков
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                color="warning"
                                size="medium"
                                onClick={() => setDevTempErrorDialogOpen(true)}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        backgroundColor: 'rgba(255, 152, 0, 0.1)'
                                    }
                                }}
                            >
                                Перевести заказ во временную ошибку
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                size="medium"
                                onClick={() => setDevFatalErrorDialogOpen(true)}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        backgroundColor: 'rgba(244, 67, 54, 0.1)'
                                    }
                                }}
                            >
                                Перевести заказ в фатальную ошибку
                            </Button>
                        </Box>
                    </Box>
                )}
            </>
        )}

        {record.status === ProgressOrderStatus.WAITING_FOR_SHIPMENT && (
            <Box sx={{ 
                mt: 3, 
                mb: 4,
                display: 'flex', 
                justifyContent: 'center',
                gap: 2,
                px: 2,
                py: 1.5
            }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<LocalShippingIcon />}
                    onClick={handleInDelivery}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Перевести в статус "Доставляется"
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    size="large"
                    startIcon={<ScheduleIcon />}
                    onClick={handleDeliveryExpired}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Перевести в статус "Доставка истекла"
                </Button>
            </Box>
        )}

        {(record.status === ProgressOrderStatus.ERROR_WHEN_CREATING_DELIVERY_ORDER ||
          record.status === ProgressOrderStatus.REQUIRED_SENDER_INFORMATION) && (
            <Box sx={{ 
                mt: 3, 
                mb: 4,
                display: 'flex', 
                justifyContent: 'center',
                px: 2,
                py: 1.5
            }}>
                <Button
                    variant="contained"
                    color="info"
                    size="large"
                    startIcon={<LocalShippingIcon />}
                    onClick={() => setRetryDeliveryOrderDialogOpen(true)}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    {record.status === ProgressOrderStatus.REQUIRED_SENDER_INFORMATION
                        ? 'Перевести в создание заказа'
                        : 'Сделать перезапрос заказа'}
                </Button>
            </Box>
        )}

        {(record.status === ProgressOrderStatus.CANCELLED_IN_DELIVERY_BY_CUSTOMER ||
          record.status === ProgressOrderStatus.CANCELLED_AFTER_DELIVERED_BY_CUSTOMER ||
          record.status === ProgressOrderStatus.CANCELLED_BY_EXPIRED_PICKING) && (
            <Box sx={{ 
                mt: 3, 
                mb: 4,
                display: 'flex', 
                justifyContent: 'center',
                px: 2,
                py: 1.5
            }}>
                <Button
                    variant="contained"
                    color="warning"
                    size="large"
                    startIcon={<LocalShippingIcon />}
                    onClick={() => setReturnDialogOpen(true)}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Перевести в статус "Возврат"
                </Button>
            </Box>
        )}

        {record.status === ProgressOrderStatus.ORDER_IS_RETURNING_TO_SELLER && (
            <Box sx={{ 
                mt: 3, 
                mb: 4,
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.5
            }}>
                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<LocalShippingIcon />}
                    onClick={() => setDeliveredBackConfirmationDialogOpen(true)}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Перевести в статус "Доставлено обратно"
                </Button>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setPvzDialogOpen(true)}
                        sx={{
                            px: 3,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        Добавить/Изменить информацию по возвратному ПВЗ
                    </Button>
                    
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => setArrivalDateDialogOpen(true)}
                        sx={{
                            px: 3,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            '&:hover': {
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        Выставить дату примерного прибытия посылки
                    </Button>
                </Box>
            </Box>
        )}

        {record.status === ProgressOrderStatus.ORDER_RETURNED_TO_SELLER_POINT && (
            <Box sx={{ 
                mt: 3, 
                mb: 4,
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                px: 2,
                py: 1.5
            }}>
                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<LocalShippingIcon />}
                    onClick={handleReturnedOrderPickedBySeller}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Перевести в статус "Обратный заказ взят продавцом"
                </Button>
                
                <Button
                    variant="outlined"
                    size="large"
                    onClick={() => setPvzDialogOpen(true)}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': {
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Добавить/Изменить информацию по возвратному ПВЗ
                </Button>
            </Box>
        )}

        {record.status === ProgressOrderStatus.IN_DELIVERY && (
            <Box sx={{ 
                mt: 3, 
                mb: 4,
                display: 'flex', 
                justifyContent: 'center',
                gap: 2,
                px: 2,
                py: 1.5
            }}>
                <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<LocalShippingIcon />}
                    onClick={handleDeliveredToPvz}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        boxShadow: 2,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Перевести в статус "Доставлено в ПВЗ"
                </Button>
                
                <Button
                    variant="outlined"
                    size="large"
                    onClick={() => setDeliveryArrivalDateDialogOpen(true)}
                    sx={{
                        px: 3,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 2,
                        '&:hover': {
                            transform: 'translateY(-1px)'
                        },
                        transition: 'all 0.2s ease-in-out'
                    }}
                >
                    Выставить дату примерного прибытия посылки
                </Button>
            </Box>
        )}

        {record.status === ProgressOrderStatus.IN_DELIVERY_POINT && (
            <>
                <Box sx={{ 
                    mt: 3, 
                    mb: 4,
                    display: 'flex', 
                    justifyContent: 'center',
                    gap: 2,
                    px: 2,
                    py: 1.5
                }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        startIcon={<LocalShippingIcon />}
                        onClick={() => setReceivedConfirmationDialogOpen(true)}
                        sx={{
                            px: 3,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            boxShadow: 2,
                            '&:hover': {
                                boxShadow: 4,
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        Перевести в статус "Получено"
                    </Button>
                </Box>

                {/* Секция для ручной отмены */}
                {process.env.NODE_ENV === 'development' && (
                    <Box sx={{ 
                        mt: 2, 
                        mb: 4,
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        px: 2,
                        py: 2,
                        border: '2px dashed #d32f2f',
                        borderRadius: 2,
                        backgroundColor: 'rgba(211, 47, 47, 0.05)'
                    }}>
                        <Typography variant="h6" color="error.main" sx={{ fontWeight: 600 }}>
                            ⚠️ Ручная отмена
                        </Typography>
                        <Typography variant="body2" color="error.main" sx={{ textAlign: 'center', maxWidth: '600px' }}>
                            Это опасный раздел! Использовать только по согласованию по каждому конкретному случаю.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                color="warning"
                                size="medium"
                                onClick={() => setCancelledByExpiredPickingDialogOpen(true)}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        backgroundColor: 'rgba(255, 152, 0, 0.1)'
                                    }
                                }}
                            >
                                Отменить (покупатель не забрал из ПВЗ)
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                size="medium"
                                onClick={() => setCancelledAfterDeliveredDialogOpen(true)}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderWidth: 2,
                                        backgroundColor: 'rgba(255, 152, 0, 0.1)'
                                    }
                                }}
                            >
                                Отменить (покупатель отказался от вещи в ПВЗ)
                            </Button>
                        </Box>
                    </Box>
                )}
            </>
        )}


        </>
    );
};
